import { useCallback, useDeferredValue, useEffect, useState, type FormEvent } from "react";
import { Archive } from "@phosphor-icons/react/Archive";
import { File as FileIcon } from "@phosphor-icons/react/File";
import { Image } from "@phosphor-icons/react/Image";
import { Link } from "@phosphor-icons/react/Link";
import { Plus } from "@phosphor-icons/react/Plus";
import { SlidersHorizontal } from "@phosphor-icons/react/SlidersHorizontal";
import { TextT } from "@phosphor-icons/react/TextT";
import { Trash } from "@phosphor-icons/react/Trash";
import { Tray } from "@phosphor-icons/react/Tray";
import { useWebRuntime } from "@tchoma/runtime-web";
import {
  AppShell,
  Button,
  EmptyState,
  List,
  ListRow,
  Notice,
  PageHeader,
  Sheet,
  SheetActions,
  StatusPill,
  TchomaLogo,
  TextField,
  type NavigationItem,
} from "@tchoma/ui-riri";
import { confirmCaptureHandoff, type CaptureHandoffInput } from "./capture-handoff";
import { DataOwnershipPanel } from "./DataOwnershipPanel";
import type { InboxEntry, InboxKind } from "./model";
import { openInboxRepository } from "./repository";
import type { InboxRouteId } from "./routes";

const NAVIGATION: readonly NavigationItem[] = [
  { id: "inbox", label: "Inbox", href: "/", icon: <Tray /> },
  { id: "archive", label: "Archive", href: "/archive", icon: <Archive /> },
  { id: "settings", label: "Settings", href: "/settings", icon: <SlidersHorizontal /> },
];

export function App({ initialCapture = { status: "absent" } }: { initialCapture?: CaptureHandoffInput }) {
  const runtime = useWebRuntime<InboxRouteId>();
  const [entries, setEntries] = useState<InboxEntry[]>([]);
  const [query, setQuery] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query);
  const route = runtime.state.route.id;
  const archived = route === "archive";

  const reload = useCallback(async () => {
    if (route === "settings" || route === "capture") return;
    setBusy(true);
    try {
      const repository = await openInboxRepository();
      setEntries(await repository.list({ archived, query: deferredQuery }));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Inbox could not open local storage.");
    } finally {
      setBusy(false);
    }
  }, [archived, deferredQuery, route]);

  useEffect(() => { void reload(); }, [reload]);

  const content = route === "settings"
    ? <SettingsScreen />
    : route === "capture"
      ? <CapturePreviewScreen
          capture={initialCapture}
          onCancel={() => runtime.actions.navigate("inbox")}
          onManual={() => {
            runtime.actions.navigate("inbox");
            setComposeOpen(true);
          }}
          onSaved={() => runtime.actions.navigate("inbox")}
        />
      : <EntriesScreen
        archived={archived}
        busy={busy}
        entries={entries}
        error={error}
        query={query}
        onQuery={setQuery}
        onCompose={() => setComposeOpen(true)}
        onChanged={reload}
      />;

  return (
    <AppShell
      brand="Inbox"
      brandNote="local by default"
      brandMark={<TchomaLogo />}
      navigation={NAVIGATION}
      currentNavigationId={route}
      actions={<StatusPill tone={runtime.state.diagnostics.online ? "ready" : "warning"}>{runtime.state.diagnostics.online ? "On device" : "Offline · safe"}</StatusPill>}
      onNavigate={(item) => runtime.actions.navigate(item.id as InboxRouteId)}
    >
      {content}
      <CaptureSheet open={composeOpen} onOpenChange={setComposeOpen} onSaved={reload} />
    </AppShell>
  );
}

function EntriesScreen(props: {
  archived: boolean;
  busy: boolean;
  entries: InboxEntry[];
  error: string | null;
  query: string;
  onQuery: (value: string) => void;
  onCompose: () => void;
  onChanged: () => Promise<void>;
}) {
  return (
    <>
      <PageHeader
        eyebrow={props.archived ? "Kept, but quiet" : "Anything, for later"}
        title={props.archived ? "Archive" : "Inbox"}
        description={props.archived ? "Restore something when it matters again." : "Save the thought first. Organise only if useful."}
        actions={!props.archived ? <Button onClick={props.onCompose}><Plus weight="bold" /> Add</Button> : undefined}
      />
      {props.error ? <Notice tone="error" title="Local storage needs attention">{props.error}</Notice> : null}
      <section className="inbox-surface" aria-label={props.archived ? "Archived entries" : "Saved entries"}>
        <div className="inbox-search">
          <TextField
            label="Search saved things"
            type="search"
            value={props.query}
            placeholder="A word, site or filename"
            onChange={(event) => props.onQuery(event.currentTarget.value)}
          />
        </div>
        <div className="inbox-results">
          {props.busy ? <p className="inbox-muted" role="status">Opening your on-device inbox…</p> : null}
          {!props.busy && props.entries.length === 0 ? (
            <EmptyState
              icon={props.archived ? <Archive /> : <Tray />}
              title={props.query ? "Nothing matches that search" : props.archived ? "Archive is empty" : "A calm place to capture"}
              action={!props.archived && !props.query ? <Button onClick={props.onCompose}>Save the first thing</Button> : undefined}
            >
              {props.query ? "Try fewer words." : props.archived ? "Archived items will wait here." : "Text, links, images and files stay useful offline."}
            </EmptyState>
          ) : (
            <List>{props.entries.map((entry) => <EntryRow key={entry.id} entry={entry} onChanged={props.onChanged} />)}</List>
          )}
        </div>
      </section>
    </>
  );
}

function EntryRow({ entry, onChanged }: { entry: InboxEntry; onChanged: () => Promise<void> }) {
  const [working, setWorking] = useState(false);
  const icon = kindIcon(entry.data.kind);
  const description = (entry.data.sourceUrl ?? entry.data.body) || entry.data.attachment?.name || "Saved locally";
  const update = async (action: "archive" | "restore" | "delete") => {
    setWorking(true);
    const repository = await openInboxRepository();
    try {
      if (action === "delete") await repository.remove(entry);
      else await repository.setArchived(entry, action === "archive");
      await onChanged();
    } finally {
      setWorking(false);
    }
  };
  return (
    <ListRow
      className="inbox-entry-row"
      title={entry.data.title}
      description={description}
      leading={icon}
      trailing={<span className="inbox-row-actions">
        <Button tone="quiet" disabled={working} aria-label={entry.data.archived ? `Restore ${entry.data.title}` : `Archive ${entry.data.title}`} onClick={() => void update(entry.data.archived ? "restore" : "archive")}>
          {entry.data.archived ? <Tray /> : <Archive />}
        </Button>
        {entry.data.archived ? <Button tone="quiet" disabled={working} aria-label={`Delete ${entry.data.title}`} onClick={() => void update("delete")}><Trash /></Button> : null}
      </span>}
    />
  );
}

function CaptureSheet({ open, onOpenChange, onSaved }: { open: boolean; onOpenChange: (open: boolean) => void; onSaved: () => Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const form = event.currentTarget;
    const data = new FormData(form);
    const selected = data.get("attachment");
    const file = selected instanceof globalThis.File && selected.size > 0 ? selected : undefined;
    try {
      const repository = await openInboxRepository();
      await repository.capture({
        text: String(data.get("text") ?? ""),
        title: String(data.get("title") ?? ""),
        ...(file ? { file } : {}),
      });
      form.reset();
      setError(null);
      onOpenChange(false);
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That item could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet open={open} title="Save to Inbox" description="One useful detail is enough." onOpenChange={onOpenChange}>
      <form className="inbox-capture" onSubmit={handleSubmit}>
        <TextField label="Text or URL" name="text" placeholder="Paste or type anything" autoFocus />
        <TextField label="Title" name="title" description="Optional — Inbox can make one for you." />
        <label className="inbox-file-field">
          <span>Image or file</span>
          <input name="attachment" type="file" />
        </label>
        {error ? <Notice tone="error" title="Not saved">{error}</Notice> : null}
        <SheetActions><Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save on this device"}</Button></SheetActions>
      </form>
    </Sheet>
  );
}

function SettingsScreen() {
  const runtime = useWebRuntime<InboxRouteId>();
  const diagnostics = [
    ["Connection", runtime.state.diagnostics.online ? "Online" : "Offline"],
    ["Installed", runtime.state.diagnostics.standalone ? "Standalone" : "Browser tab"],
    ["Offline shell", runtime.state.serviceWorker.status],
    ["Cloud sync", "Off — local-only"],
  ] as const;
  return (
    <>
      <PageHeader eyebrow="Yours means exportable" title="Settings" description="Inbox has no account and does not prepare cloud sync operations." />
      <DataOwnershipPanel />
      <section className="inbox-settings-section" aria-labelledby="inbox-diagnostics-title">
        <h2 id="inbox-diagnostics-title">Diagnostics</h2>
        <List>{diagnostics.map(([title, value]) => <ListRow key={title} title={title!} trailing={<StatusPill>{value}</StatusPill>} />)}</List>
      </section>
    </>
  );
}

function CapturePreviewScreen({
  capture,
  onCancel,
  onManual,
  onSaved,
}: {
  capture: CaptureHandoffInput;
  onCancel: () => void;
  onManual: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const confirm = async () => {
    if (capture.status !== "ready") return;
    setSaving(true);
    try {
      const repository = await openInboxRepository();
      await confirmCaptureHandoff(capture, async (text) => { await repository.capture({ text }); });
      setError(false);
      onSaved();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader eyebrow="Shortcut handoff" title="Confirm capture" description="Inbox saves only after you approve the visible content below." />
      <section className="inbox-capture-preview" aria-label="Shortcut capture preview">
        {capture.status === "ready" ? (
          <>
            <Notice tone="warning" title="Nothing has been saved yet">
              This route carries only the visible small text or URL. Image and file bytes are not attached; cancel and add those manually.
            </Notice>
            <div className="inbox-preview-content">
              <span>{capture.kind === "url" ? "URL" : "Text"} · {capture.byteLength} bytes</span>
              <pre>{capture.text}</pre>
            </div>
            {error ? <Notice tone="error" title="Not saved">Local storage could not confirm the write. The preview remains available.</Notice> : null}
            <div className="inbox-preview-actions">
              <Button disabled={saving} onClick={() => void confirm()}>{saving ? "Saving…" : "Confirm and save"}</Button>
              <Button tone="secondary" disabled={saving} onClick={onCancel}>Cancel</Button>
              <Button tone="quiet" disabled={saving} onClick={onManual}>Add image or file manually</Button>
            </div>
          </>
        ) : (
          <>
            <Notice tone={capture.status === "replay" ? "warning" : "error"} title={capture.status === "replay" ? "Capture already handled" : "No safe capture payload"}>
              Nothing was saved. The handoff was missing, malformed, too large, or already consumed. Open the manual form instead.
            </Notice>
            <div className="inbox-preview-actions">
              <Button onClick={onManual}>Add manually</Button>
              <Button tone="secondary" onClick={onCancel}>Back to Inbox</Button>
            </div>
          </>
        )}
      </section>
    </>
  );
}

function kindIcon(kind: InboxKind) {
  if (kind === "url") return <Link />;
  if (kind === "image") return <Image />;
  if (kind === "file") return <FileIcon />;
  return <TextT />;
}
